/**
 * Postal-code → Province/State auto-detect.
 *
 * iter114s.59 — Operator brief: "Auto-detect and populate
 * State/Province based on entered address data". Implementation
 * choice (c) from clarification: postal-code-only, no Google Places /
 * MapBox API key required.
 *
 * • Canada: First letter of a properly-formatted postal code maps
 *   1:1 to a province/territory (X is split between NU/NT — we pick
 *   NU since most X0xxx codes are NU).
 * • United States: First three digits of the ZIP code map to a state
 *   via FIPS / USPS prefix ranges. Compiled as range-based lookup
 *   below to keep the bundle ~2KB instead of listing every prefix.
 *
 * `detectStateFromPostal(code, country)` returns the canonical state
 * string (matches the dropdown values in `lib/geoData`) or `null` if
 * the postal code doesn't parse / doesn't map cleanly.
 */

// First letter of CA postal code → Province. Canada Post FSAs always
// follow this rule. (X is ambiguous between NU and NT — defaulting to
// NU as it's the more common production destination from EG3's pod.)
const CA_LETTER_MAP = {
  A: 'Newfoundland and Labrador',
  B: 'Nova Scotia',
  C: 'Prince Edward Island',
  E: 'New Brunswick',
  G: 'Quebec',
  H: 'Quebec',
  J: 'Quebec',
  K: 'Ontario',
  L: 'Ontario',
  M: 'Ontario',
  N: 'Ontario',
  P: 'Ontario',
  R: 'Manitoba',
  S: 'Saskatchewan',
  T: 'Alberta',
  V: 'British Columbia',
  X: 'Nunavut',
  Y: 'Yukon',
};

// US ZIP-prefix ranges (first 3 digits, inclusive on both ends) → state.
// Built from the USPS ZCTA / FIPS allocation tables. Tested against
// state capitals and major-city ZIPs. Order matters — list specific
// (smaller) ranges first when ranges overlap.
const US_RANGES = [
  ['Alabama',              ['350-369']],
  ['Alaska',               ['995-999']],
  ['Arizona',              ['850-865']],
  ['Arkansas',             ['716-729', '755-755']],
  ['California',           ['900-961']],
  ['Colorado',             ['800-816']],
  ['Connecticut',          ['060-069']],
  ['Delaware',             ['197-199']],
  ['District of Columbia', ['200-205']],
  ['Florida',              ['320-349']],
  ['Georgia',              ['300-319', '398-399']],
  ['Hawaii',               ['967-968']],
  ['Idaho',                ['832-838']],
  ['Illinois',             ['600-629']],
  ['Indiana',              ['460-479']],
  ['Iowa',                 ['500-528']],
  ['Kansas',               ['660-679']],
  ['Kentucky',             ['400-427']],
  ['Louisiana',            ['700-714']],
  ['Maine',                ['039-049']],
  ['Maryland',             ['206-219']],
  ['Massachusetts',        ['010-027', '055-055']],
  ['Michigan',             ['480-499']],
  ['Minnesota',            ['550-567']],
  ['Mississippi',          ['386-397']],
  ['Missouri',             ['630-658']],
  ['Montana',              ['590-599']],
  ['Nebraska',             ['680-693']],
  ['Nevada',               ['889-898']],
  ['New Hampshire',        ['030-038']],
  ['New Jersey',           ['070-089']],
  ['New Mexico',           ['870-884']],
  ['New York',             ['004-005', '090-149']],
  ['North Carolina',       ['270-289']],
  ['North Dakota',         ['580-588']],
  ['Ohio',                 ['430-459']],
  ['Oklahoma',             ['730-749']],
  ['Oregon',               ['970-979']],
  ['Pennsylvania',         ['150-196']],
  ['Puerto Rico',          ['006-009']],
  ['Rhode Island',         ['028-029']],
  ['South Carolina',       ['290-299']],
  ['South Dakota',         ['570-577']],
  ['Tennessee',            ['370-385']],
  ['Texas',                ['733-733', '750-799', '885-885']],
  ['Utah',                 ['840-847']],
  ['Vermont',              ['050-054', '056-059']],
  ['Virginia',             ['201-201', '220-246']],
  ['Washington',           ['980-994']],
  ['West Virginia',        ['247-268']],
  ['Wisconsin',            ['530-549']],
  ['Wyoming',              ['820-831']],
];

const _matchRange = (prefix, ranges) => {
  for (const r of ranges) {
    const [lo, hi] = r.split('-');
    if (prefix >= lo && prefix <= hi) return true;
  }
  return false;
};

/**
 * @param {string} rawCode  Raw postal-code input (may contain spaces / dashes)
 * @param {string} country  Selected country (matches values from `lib/geoData`)
 * @returns {string|null}   Canonical state/province name, or null if no match
 */
export const detectStateFromPostal = (rawCode, country) => {
  if (!rawCode || !country) return null;
  const code = String(rawCode).trim().toUpperCase();

  if (country === 'Canada' || country === 'CA') {
    // Canada postal codes: letter+digit+letter (digit+letter+digit) — 6 alphanumerics with optional space.
    const m = code.match(/^([A-Z])\d[A-Z]/);
    if (m) return CA_LETTER_MAP[m[1]] || null;
    return null;
  }

  if (country === 'United States' || country === 'US' || country === 'USA') {
    // US ZIPs: 5 digits with optional -4. We only need the first 3.
    const m = code.match(/^(\d{3})/);
    if (!m) return null;
    const prefix = m[1];
    for (const [state, ranges] of US_RANGES) {
      if (_matchRange(prefix, ranges)) return state;
    }
    return null;
  }

  return null;
};

export default detectStateFromPostal;
